import { allFromSnapshot, allFromSnapshotByUid, ClientFirestoreAdapter, firestoreFieldValueDelete, firstFromSnapshot, getFirestoreErrorCode, isAdminFirestoreAdapter, oneFromSnapshot, } from "@buildwithflux/firestore-compatibility-layer";
import { OrganizationVisibility, UsernameTakenError, } from "@buildwithflux/models";
import { guid } from "@buildwithflux/shared";
import { chunk } from "lodash";
import { OrganizationRepository, } from "./OrganizationRepository";
export class FirestoreOrganizationRepository extends OrganizationRepository {
    db;
    userRepository;
    organizationPrivateMetadataRepository;
    logger;
    firestoreOrganizationChangeSubscriptions = new Map();
    constructor(db, userRepository, organizationPrivateMetadataRepository, logger) {
        super();
        this.db = db;
        this.userRepository = userRepository;
        this.organizationPrivateMetadataRepository = organizationPrivateMetadataRepository;
        this.logger = logger;
    }
    /**
     * Subscribes to all changes to the organization
     *
     * A little different from the user version, because we can reference organizations directly by uid (whereas users
     * are keyed by handle)
     *
     * @inheritDoc
     */
    subscribeToOrganization(organizationUid, onChange) {
        const organizationRef = this.db.organization(organizationUid);
        // NOTE: custom cleanup logic here: we check to see if there are any subscribers left.  As long as there are,
        // we keep our subscription open.  Once there aren't any remaining, we shut it down.
        const unsubscribe = this.subscriptionMgr.addSubscription(organizationUid, onChange, () => {
            const existingSubCount = this.subscriptionMgr.listenerCount(organizationUid);
            if (existingSubCount === 0) {
                const firestoreUnsub = this.firestoreOrganizationChangeSubscriptions.get(organizationUid);
                if (firestoreUnsub) {
                    firestoreUnsub();
                    this.firestoreOrganizationChangeSubscriptions.delete(organizationUid);
                }
            }
        });
        // NOTE: We're using a multiplexing pattern here.  There may be multiple logical subscribers to firestore,
        // but we create exactly one subscription here.  When the subscription is triggered, it causes all of the
        // logical subscriptions to fire as well.
        const existing = this.firestoreOrganizationChangeSubscriptions.get(organizationUid);
        if (!existing) {
            const firestoreUnsubscriber = organizationRef.onSnapshot((docSnapshot) => {
                const data = docSnapshot.data();
                if (data === undefined) {
                    this.subscriptionMgr.notify(organizationUid, { type: "deleted" });
                }
                else {
                    this.subscriptionMgr.notify(organizationUid, {
                        type: "updated",
                        data,
                    });
                }
            }, (error) => {
                this.subscriptionMgr.notify(organizationUid, {
                    type: "error",
                    error,
                });
            });
            this.firestoreOrganizationChangeSubscriptions.set(organizationUid, firestoreUnsubscriber);
        }
        return unsubscribe;
    }
    /** @inheritDoc */
    async create(organizationData, currentUserService) {
        /**
         * For typing reasons (different transaction types in client/admin Firestore SDKs until we upgrade to v9), only
         * the web SDK transaction is currently implemented
         */
        if (!(this.db instanceof ClientFirestoreAdapter)) {
            throw new Error("Create is only implemented for clients");
        }
        const db = this.db;
        const currentUser = currentUserService.getCurrentUser();
        if (!currentUser || currentUser.isAnonymous) {
            throw new Error("Must be an authenticated user to create an organization");
        }
        const { organization, member, handleMapping } = this.getCreationData({
            organizationData,
            ownerUser: currentUser,
        });
        // TODO: zod to remove stray properties?
        await db.runTransaction(async (t) => {
            const handleMappingRef = db.handleMapping(organization.handle);
            const existingHandleMapping = (await t.get(handleMappingRef)).data();
            if (existingHandleMapping && existingHandleMapping.organizationUid !== organization.uid) {
                throw new UsernameTakenError();
            }
            const organizationRef = db.organization(organization.uid);
            let existingOrganization = undefined;
            try {
                existingOrganization = (await t.get(organizationRef)).data();
            }
            catch (error) {
                if (getFirestoreErrorCode(error) === "permission-denied" ||
                    getFirestoreErrorCode(error) === "unknown") {
                    // NOTE: get is denied by security rule if org doesn't exist, or equally if the user does not have access
                    // We can't do better than to attempt to create it here, if we failed this initial test, otherwise
                    // we'd be giving away the existence of non-public organizations
                    // In production, the code is unknown, not permission-denied, unfortunately
                    // So, we silence this error
                }
                else {
                    throw error;
                }
            }
            if (existingOrganization) {
                throw new Error("Cannot create organization: already exists with the same UID");
            }
            // Ready to attempt to write new organization
            t.set(organizationRef, organization);
            t.set(db.organizationMember(organization.uid, currentUser.uid), member);
            if (handleMapping) {
                t.set(handleMappingRef, handleMapping);
            }
        });
        void this.userRepository.updateOrganizationMembershipSentinel(currentUser.uid);
        return organization;
    }
    /** @inheritDoc */
    async getPublicOrganizationByHandle(handle) {
        return firstFromSnapshot(await this.db
            .organizationCollection()
            .where("handle", "==", handle)
            .where("visibility", "==", OrganizationVisibility.Values.public)
            .get(), () => undefined);
    }
    /** @inheritDoc */
    async save(organizationData) {
        return this.db
            .organizationCollection()
            .doc(organizationData.uid)
            .set({ ...organizationData, updatedAt: Date.now(), createdAt: Date.now(), partsCount: 0, documentsCount: 0 }, { merge: true })
            .then();
    }
    /** @inheritDoc */
    async update(organizationUid, updateWith) {
        const organizationDocRef = this.db.organizationCollection().doc(organizationUid);
        // If the organization has been deleted, refuse to update it further
        if (!(await organizationDocRef.get()).exists) {
            return;
        }
        return this.db
            .organizationCollection()
            .doc(organizationUid)
            .set({ ...updateWith, updatedAt: Date.now() }, { merge: true })
            .then();
    }
    /** @inheritDoc */
    async addMember(organization, userUid, userHandle, role = "member") {
        if ((await this.db.organizationMember(organization.uid, userUid).get()).exists) {
            await this.db.organizationMember(organization.uid, userUid).set({
                role,
                updatedAt: Date.now(),
            }, { merge: true });
        }
        else {
            await this.db.organizationMember(organization.uid, userUid).set({
                organizationUid: organization.uid,
                userUid,
                userHandle,
                role,
                enterpriseUid: organization.enterpriseUid,
                visibility: organization.visibility,
                updatedAt: Date.now(),
                createdAt: Date.now(),
            });
        }
    }
    /** @inheritDoc */
    async removeMember(organizationUid, userUid) {
        const current = await this.getUserRole(organizationUid, userUid);
        // Don't allow removing the last remaining owner
        if (current === "owner") {
            // Can't do this transactionally because we can't do a query in a transaction
            const snapshot = await this.db
                .organizationMembersCollection(organizationUid)
                .where("userUid", "!=", userUid)
                .where("role", "==", "owner")
                .limit(1)
                .get();
            if (snapshot.docs.length === 0) {
                throw new OrganizationCannotRemoveLastOwnerError();
            }
        }
        await this.db.organizationMember(organizationUid, userUid).delete();
    }
    /** @inheritDoc */
    async getAllDecoratedOrganizationMembersForCurrentUser(currentUserService) {
        const currentUserUid = currentUserService.getCurrentUser()?.uid;
        if (!currentUserUid) {
            return [];
        }
        return this.getAllDecoratedOrganizationMembersForUserUid(currentUserUid);
    }
    /** @inheritDoc */
    async getAllOrganizationMembersForCurrentUser(currentUserService) {
        const currentUserUid = currentUserService.getCurrentUser()?.uid;
        if (!currentUserUid) {
            return [];
        }
        return this.getAllOrganizationMembersForUserUid(currentUserUid);
    }
    /** @inheritDoc */
    async getPublicOrganizationMembersForUserUid(userUid) {
        const organizationMembers = allFromSnapshot(await this.db
            .organizationMembersCollectionGroup()
            .where("userUid", "==", userUid)
            .where("visibility", "==", "public")
            .get());
        if (organizationMembers.length === 0) {
            // Prevents 'Invalid Query. A non-empty array is required for 'in' filters'
            return [];
        }
        const byOrganizationUid = await this.getPublicOrganizationsByUid(organizationMembers);
        return (organizationMembers
            // Assuming if we couldn't get the organization for the membership, something is wrong (a mismatched UID in a membership?)
            .filter((member) => byOrganizationUid[member.organizationUid])
            .flatMap((member) => {
            const organization = byOrganizationUid[member.organizationUid];
            if (!organization) {
                return [];
            }
            return [{ ...member, organization }];
        }));
    }
    async getOrganizationMemberForCurrentUser(organizationUid, currentUserService) {
        const currentUserUid = currentUserService.getCurrentUser()?.uid;
        if (!currentUserUid) {
            return undefined;
        }
        return this.getOrganizationMemberForCurrentUserUid(organizationUid, currentUserUid);
    }
    /** @inheritDoc */
    async getOrganizationMemberForCurrentUserUid(organizationUid, currentUserUid) {
        return this.getOrganizationMember(organizationUid, currentUserUid);
    }
    /** @inheritDoc */
    async getAllOrganizationMembers(organizationUid) {
        return allFromSnapshot(await this.db.organizationMembersCollection(organizationUid).get());
    }
    /** @inheritDoc */
    async getUserRole(organizationUid, userUid) {
        try {
            return oneFromSnapshot(await this.db.organizationMember(organizationUid, userUid).get(), () => undefined)
                ?.role;
        }
        catch (error) {
            if (getFirestoreErrorCode(error) === "permission-denied" || getFirestoreErrorCode(error) === "unknown") {
                // This error means either the queried user has no role, or the current user does not have enough access to know whether the user has a role
                // In production, the code is unknown, not permission-denied, unfortunately
                return undefined;
            }
            else {
                throw error;
            }
        }
    }
    /** @inheritDoc */
    async setUserRole(organizationUid, userUid, role) {
        const ref = this.db.organizationMember(organizationUid, userUid);
        const snapshot = await ref.get();
        if (!snapshot.exists) {
            throw new Error("Organization member does not exist");
        }
        await ref.set({ role, updatedAt: Date.now() }, { merge: true });
    }
    /**
     * @inheritDoc
     */
    createMembersStream(options) {
        const { organizationUid, currentUserOrganizationRole, limit } = options;
        let streamEnded = false;
        let lastMember = null;
        const repository = this;
        return {
            fetchNextPage(onSuccess, onError) {
                if (streamEnded) {
                    onSuccess([]);
                    return () => { };
                }
                let query = repository.db
                    .organizationMembersCollection(organizationUid)
                    .limit(limit)
                    .orderBy("userHandle", "asc");
                if (!currentUserOrganizationRole) {
                    query = query.where("visibility", "==", "public");
                }
                if (lastMember) {
                    query = query.startAfter(lastMember);
                }
                return query.onSnapshot((snapshot) => {
                    lastMember = snapshot.docs[snapshot.docs.length - 1] ?? null;
                    onSuccess(snapshot.docs.map((it) => it.data()));
                }, onError);
            },
            reset() {
                lastMember = null;
                streamEnded = false;
            },
        };
    }
    async updateDocumentDataSentinel(ownerOrganizationUid) {
        await this.update(ownerOrganizationUid, {
            document_data_sentinel: guid(),
        });
    }
    /**  @inheritDoc */
    async delete(organization) {
        await this.db.organization(organization.uid).delete();
    }
    /** @inheritDoc */
    async *getAll() {
        const pageSize = 100;
        let query = this.db.organizationCollection().limit(pageSize);
        let lastOrganization = null;
        while (true) {
            if (lastOrganization) {
                query = query.startAfter(lastOrganization);
            }
            const snapshot = await query.get();
            if (snapshot.empty) {
                break;
            }
            yield* allFromSnapshot(snapshot);
            lastOrganization = snapshot.docs[snapshot.docs.length - 1] ?? null;
        }
    }
    /** @inheritDoc */
    async *getAllMembersWithRole(organizationUid, organizationRole) {
        if (!isAdminFirestoreAdapter(this.db)) {
            // Security rules prevent querying in this way
            throw new Error("This method cannot be used from the frontend");
        }
        const pageSize = 100;
        let query = this.db
            .organizationMembersCollection(organizationUid)
            .where("role", "==", organizationRole)
            .limit(pageSize);
        let lastOrganizationMember = null;
        while (true) {
            if (lastOrganizationMember) {
                query = query.startAfter(lastOrganizationMember);
            }
            const snapshot = await query.get();
            if (snapshot.empty) {
                break;
            }
            yield* allFromSnapshot(snapshot);
            lastOrganizationMember = snapshot.docs[snapshot.docs.length - 1] ?? null;
        }
    }
    /** @inheritDoc */
    async setActiveProductUid(organizationUid, activeProductUid) {
        const ref = this.db.organization(organizationUid);
        const snapshot = await ref.get();
        if (!snapshot.exists) {
            throw new Error("Organization does not exist");
        }
        await ref.update({ activeProductUid: activeProductUid ?? (await firestoreFieldValueDelete(this.db)) });
    }
    /** @inheritDoc */
    async getOrganizationMember(organizationUid, userUid) {
        const organizationSnapshot = this.db.organization(organizationUid).get();
        const memberSnapshot = this.db.organizationMember(organizationUid, userUid).get();
        const metadataSnapshot = this.db.organizationPrivateMetadata(organizationUid).get();
        const organization = oneFromSnapshot(await organizationSnapshot, () => undefined);
        const member = oneFromSnapshot(await memberSnapshot, () => undefined);
        const metadata = oneFromSnapshot(await metadataSnapshot, () => ({
            uid: organizationUid,
        }));
        if (organization && member) {
            return {
                ...member,
                organization,
                metadata,
            };
        }
        return undefined;
    }
    /** @inheritDoc */
    async getByUidWithoutCache(organizationUid) {
        return oneFromSnapshot(await this.db.organization(organizationUid).get(), () => undefined);
    }
    /**
     * Used internally to implement the public getAllOrganizationMembersForCurrentUser method, and in the child
     * FirestoreAdminOrganizationRepository to implement the public getAllOrganizationMembersForUserUid method.
     *
     * This isn't exposed directly on the client repository: it's exposed in those two ways to enforce callers to abide
     * by the security rules (which would otherwise block the query). It is exposed directly on the admin repository.
     */
    async getAllOrganizationMembersForUserUid(userUid) {
        return allFromSnapshot(await this.db.organizationMembersCollectionGroup().where("userUid", "==", userUid).get());
    }
    /**
     * Used internally to implement the public getAllDecoratedOrganizationMembersForCurrentUser method, and in the child
     * FirestoreAdminOrganizationRepository to implement the public getAllDecoratedOrganizationMembersForUserUid method.
     *
     * This isn't exposed directly on the client repository: it's only exposed for the current user in the client repository,
     * to enforce callers to abide by the security rules (which would otherwise block the query). It is exposed directly
     * on the admin repository.
     */
    async getAllDecoratedOrganizationMembersForUserUid(userUid) {
        const organizationMembers = await this.getAllOrganizationMembersForUserUid(userUid);
        const getByOrganizationUid = this.getAllOrganizationsByUid(organizationMembers);
        const getMetadataByOrganizationUid = this.organizationPrivateMetadataRepository.getAllFromMembersByOrganizationUid(organizationMembers);
        const byOrganizationUid = await getByOrganizationUid;
        const metadataByOrganizationUid = await getMetadataByOrganizationUid;
        return organizationMembers
            .filter((member) => {
            // If we couldn't get the organization for the membership (e.g. because we failed to retrieve the organization), we skip the entry
            if (!byOrganizationUid[member.organizationUid]) {
                this.logger.warn(`User has membership to organization ${member.organizationUid} but its details could not be retrieved`);
            }
            // We also warn if we couldn't get the metadata, but just use an empty object in that case
            if (!metadataByOrganizationUid[member.organizationUid]) {
                this.logger.warn(`User has membership to organization ${member.organizationUid} but its metadata could not be retrieved`);
            }
            return byOrganizationUid[member.organizationUid];
        })
            .flatMap((member) => {
            const organization = byOrganizationUid[member.organizationUid];
            if (!organization) {
                return [];
            }
            const metadata = metadataByOrganizationUid[member.organizationUid] ?? { uid: member.organizationUid };
            return [{ ...member, organization, metadata }];
        });
    }
    /**
     * Performs a search for public organization data with which to decorate memberships.
     *
     * As with the method that calls this, it cannot be used for authorization, nor for querying non-public organizations.
     * Each query must contain a where clause that restricts the results to public organizations, or the query will be
     * rejected by security rules. So, this will not return non-public organizations, even if the user belongs to them.
     */
    async getPublicOrganizationsByUid(organizationMembers) {
        if (organizationMembers.length === 0) {
            // Prevents 'Invalid Query. A non-empty array is required for 'in' filters'
            return {};
        }
        // We must always perform a GET here, not a query - because a query will be rejected by Firestore rules if it
        // does not include a condition requesting public organizations only.
        if (organizationMembers.length < 10) {
            return allFromSnapshotByUid(await this.db
                .organizationCollection()
                .where("uid", "in", organizationMembers.map((it) => it.organizationUid))
                .where("visibility", "==", "public")
                .get());
        }
        let byUid = {};
        for (const group of chunk(organizationMembers, 10)) {
            const result = allFromSnapshotByUid(await this.db
                .organizationCollection()
                .where("uid", "in", group.map((it) => it.organizationUid))
                .where("visibility", "==", "public")
                .get());
            byUid = { ...byUid, ...result };
        }
        return byUid;
    }
    /**
     * Performs a search for all the given organization members, which must be memberships belonging to the current
     * user, otherwise the search will fail to find one or more of the organizations, in the event any are non-public
     *
     * If any of the given organization UIDs can't be found or cause a permissions error on retrieval, they are not
     * included in the returned object, and a log message is sent to the logger, but the overall operation will seem
     * to succeed.
     */
    async getAllOrganizationsByUid(organizationMembers) {
        if (organizationMembers.length === 0) {
            // Prevents 'Invalid Query. A non-empty array is required for 'in' filters'
            return {};
        }
        const getOneSafelyByUid = async (uid) => {
            try {
                const snapshot = await this.db.organization(uid).get();
                return oneFromSnapshot(snapshot, () => undefined);
            }
            catch (error) {
                this.logger.warn(`Could not retrieve details for organization ${uid}`, error);
                return undefined;
            }
        };
        const promises = organizationMembers.map((it) => it.organizationUid).map(getOneSafelyByUid);
        const organizations = await Promise.all(promises);
        const organizationsByUid = {};
        for (const organization of organizations) {
            if (organization) {
                organizationsByUid[organization.uid] = organization;
            }
        }
        return organizationsByUid;
    }
}
export class OrganizationCannotRemoveLastOwnerError extends Error {
    constructor() {
        super("Cannot remove the last owner of an organization");
    }
}
//# sourceMappingURL=FirestoreOrganizationRepository.js.map